import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { TaxReturn } from '@app/models/payroll/tax-return.model';
import { T4SummaryTable, TaxReturnsTable } from '@app/modules/payroll/services';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { TaxReturnService } from '@app/modules/payroll/services/tax-return.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { TranslateService } from '@ngx-translate/core';
import { CRA_FILING_DEADLINE_MAP, TAX_PORTAL_DATE_MAP } from '@payroll/constants/tax-dates';
import * as Sentry from '@sentry/angular';
import { combineLatest, from } from 'rxjs';
import { startWith } from 'rxjs/operators';

const FIRST_TAX_YEAR_AVAILABLE = 2018;

@Component({
    templateUrl: './tax-return-index.template.html',
})
export class TaxReturnIndex {
    isLoading = true;
    taxYears: number[] = [];
    craBusinessNumber: string = this.auth.company.craBusinessNumber;
    table?: T4SummaryTable | TaxReturnsTable;
    currentTaxSeason = new Date().getFullYear() - 1;
    selectedYear = this.currentTaxSeason;
    isDefaultYear = true; // Used to navigate properly when year is not in URL

    private enableT4ForTaxYear = false;

    constructor(
        public auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private translateService: TranslateService,
        private notifyService: NotifyService,
        private taxReturnService: TaxReturnService,
        private featureService: FeatureService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        this.taxYears = this.getYearsArray();

        combineLatest([
            from(this.featureService.has(FeatureFlag.qcPayroll)),
            this.route.params.pipe(startWith(this.route.snapshot.params.year)), // Get the year from the queryParams
        ])
            .pipe(takeUntilDestroyed())
            .subscribe(([isQcPayrollEnabled, { year }]) => {
                // If year wasn't provided in queryParams, it will default to the currentTaxSeason
                if (year) {
                    this.selectedYear = Number(year);
                    this.isDefaultYear = false;
                }
                // Currently two table types exist due to a change from the T4Summary model to the new Tax Return model
                if (isQcPayrollEnabled) {
                    this.table = new TaxReturnsTable(this.auth, this.selectedYear, this.isDefaultYear);
                } else {
                    this.table = new T4SummaryTable(this.auth, this.selectedYear, this.translateService);
                }
                this.isLoading = false;
            });

        this.featureService
            .has(FeatureFlag.enableT4ForTaxYear)
            .then((enableT4ForTaxYear) => (this.enableT4ForTaxYear = enableT4ForTaxYear));
    }

    get isTaxPortalOpen(): boolean {
        // Allow users to access previous years
        if (this.selectedYear < this.currentTaxSeason) {
            return true;
        }

        // Allow users to access the current year if the filing deadline has already passed
        if (new Date() > new Date(CRA_FILING_DEADLINE_MAP[this.selectedYear])) {
            return true;
        }

        // Otherwise, check feature flag
        return this.enableT4ForTaxYear;
    }

    get taxPortalDate(): string {
        return TAX_PORTAL_DATE_MAP[this.selectedYear];
    }

    getYearsArray(): number[] {
        const years = [FIRST_TAX_YEAR_AVAILABLE];
        let startYear = FIRST_TAX_YEAR_AVAILABLE;
        while (startYear < this.currentTaxSeason) {
            startYear += 1;
            years.push(startYear);
        }
        return years;
    }

    onYearChange(): void {
        this.router.navigate(['/payroll/records/tax-returns', this.selectedYear]);
    }

    generateTaxReturn(): void {
        this.isLoading = true;
        this.taxReturnService.generateTaxReturn(new TaxReturn({ year: this.selectedYear })).subscribe({
            next: (taxReturn) =>
                this.router.navigate(
                    this.isDefaultYear
                        ? [taxReturn.year.toString(), taxReturn.id.toString()]
                        : [taxReturn.id.toString()],
                    {
                        relativeTo: this.route,
                    }
                ),
            error: (error) => {
                Sentry.captureException(error);
                this.notifyService.error({
                    key: 'payroll.taxReturns.errorGenerating',
                    params: { year: this.selectedYear },
                });
                this.table?.reload();
            },
        });
    }
}
